import { Box, Button, Checkbox, Chip, Typography } from "@mui/material";
import PrintView from "src/components/shared/PrintView";
import ViewBtn from "src/components/shared/ViewBtn";
import { formatCurrency } from "src/helper/convertNum";
import convertCurrency from "src/helper/convertCurrency";
import { v4 as uuid } from "uuid";
import useInventoryNotes from "src/hooks/debt/useInventoryNotes";
import dayjs from "dayjs";
import useSummaryDebt from "src/hooks/debt/useSummaryDebt";
import useAuthGlobal from "src/context/useAuthGlobal";
import { useState } from "react";
import { CONTEXT_TYPE, TYPE } from "../const/constants";
import { renderDebtAmountByInventoryType } from "../utils/renderDebtAmountByInventoryType";
import usePreviewPdf from "./usePreviewPdf";
import { orderStatusFormatted } from "src/constant";

export default function useDebtWarehouseImportTab({ date, searchValue, filter } = {}) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailSelectedRow, setDetailSelectedRow] = useState(null);
  const [pagi, setPagi] = useState({ limit: 50, skop: 0 });
  const { userMe } = useAuthGlobal();
  const [checkedList, setCheckedList] = useState([]);
  const { onPrintWarehouseTransfer } = usePreviewPdf();
  const params = {
    ...filter,
    ...pagi,
    contextType: CONTEXT_TYPE.TRANSFER,
    type: TYPE.INBOUND,
    fromDate: date?.fromDate ?? undefined,
    toDate: date?.toDate ?? undefined,
    searchField: "code",
    searchValue: searchValue ?? undefined,
  };
  const { data } = useInventoryNotes(params);
  const { data: summaryDebt } = useSummaryDebt({
    warehouse: userMe?.currentWarehouse?._id,
    ...params,
  });

  const handleViewDetail = (item) => {
    setDetailSelectedRow(item);
  };

  const handleChangeCheckbox = (checked, item) => {
    if (checked) {
      setCheckedList((prev) => [...prev, item]);
    } else {
      setCheckedList((prev) => prev.filter((checkedItem) => checkedItem?._id !== item?._id));
    }
  };

  const handleChangeCheckboxAll = (checked) => {
    if (checked) {
      setCheckedList(data?.data?.map((item) => item));
    } else {
      setCheckedList([]);
    }
  };

  const handleView = () => {};

  const handleViewPrint = () => {};

  let bodyList = data?.data?.map((item, index) => ({
    ...item,
    stt: index + 1,
    checkbox: (
      <Checkbox
        checked={checkedList.some((checkedItem) => checkedItem?._id === item?._id)}
        onChange={(_, checked) => handleChangeCheckbox(checked, item)}
      />
    ),
    code: (
      <Typography
        sx={{ color: "primary", textDecoration: "underline", cursor: "pointer" }}
        onClick={() => onPrintWarehouseTransfer(item, orderStatusFormatted.Imported)}
      >
        {item?.code ?? "-"}
      </Typography>
    ),
    exportValue: (
      <>
        <Typography fontWeight="bold">+{formatCurrency(item?.subTotal)}</Typography>
        <Typography
          color="#989898"
          fontSize="8px"
          fontStyle="italic"
        >
          {convertCurrency(item?.subTotal)} đồng
        </Typography>
      </>
    ),
    debt: renderDebtAmountByInventoryType(item?.total, item?.type ?? TYPE.INBOUND),
    exportWarehouse: item?.warehouse?.name ? (
      <>
        <Typography fontSize="inherit">{item?.warehouse?.name}</Typography>
        <Typography
          color="#989898"
          fontSize="8px"
        >
          {item.warehouse?.createdAt ? dayjs(item.warehouse?.createdAt).format("DD/MM/YYYY") : "-"}
        </Typography>
      </>
    ) : (
      "-"
    ),
    receiveWarehouse: item?.warehouseDebt ? (
      <>
        <Typography
          fontSize="inherit"
          minWidth="100px"
        >
          {item?.warehouseDebt?.name}
        </Typography>
        <Typography
          color="#989898"
          fontSize="8px"
        >
          {item.warehouseDebt?.createdAt ? dayjs(item.warehouseDebt?.createdAt).format("DD/MM/YYYY") : "-"}
        </Typography>
      </>
    ) : (
      "-"
    ),
    paymentMethod: <Typography>{item?.paymentMethod?.name ?? "-"}</Typography>,
    status: (
      <Chip
        label={item?.status === "Paid" ? "Đã thanh toán" : "Chưa thanh toán"}
        style={{ background: item?.status === "Paid" ? "#34C75922" : "#FFB30022", color: item?.status === "Paid" ? "#34C759" : "#FFB300", fontWeight: "bold" }}
      />
    ),
    operation: (
      <Box
        display="flex"
        gap="6px"
        justifyContent="center"
      >
        <ViewBtn handleView={() => handleView(item)} />
        <PrintView handleView={() => handleViewPrint(item)} />
      </Box>
    ),
    action:
      item?.status === "Paid" ? (
        <Button
          variant="text"
          sx={{ minWidth: "150px", color: "#007AFF", backgroundColor: "#007AFF1A" }}
          onClick={() => handleViewDetail(item)}
        >
          Chi tiết thanh toán
        </Button>
      ) : (
        <Button
          variant="outlined"
          color="primary"
          sx={{ minWidth: "120px" }}
          onClick={() => setSelectedRow(item || null)}
        >
          Thanh toán
        </Button>
      ),
  }));

  let header = [
    {
      id: uuid(),
      title: (
        <Checkbox
          checked={checkedList.length === data?.data?.length}
          onChange={(_, checked) => handleChangeCheckboxAll(checked)}
        />
      ),
      align: "center",
    },
    {
      id: uuid(),
      title: "STT",
      align: "center",
    },
    {
      id: uuid(),
      title: "Phiếu nhập",
      align: "center",
    },
    {
      id: uuid(),
      title: "Kho xuất/NCC",
      align: "center",
    },
    {
      id: uuid(),
      title: "Kho nhận",
      align: "center",
    },
    {
      id: uuid(),
      title: "Giá trị phiếu xuất",
      align: "center",
    },
    {
      id: uuid(),
      title: "Công nợ",
      align: "center",
    },
    {
      id: uuid(),
      title: "Hình thức thanh toán",
      align: "center",
    },
    {
      id: uuid(),
      title: "Trạng thái",
      align: "center",
    },
    {
      id: uuid(),
      title: "Thao tác",
      align: "center",
    },
    {
      id: uuid(),
      title: "",
      align: "center",
    },
  ];

  const sortData = ["checkbox", "stt", "code", "exportWarehouse", "receiveWarehouse", "exportValue", "debt", "paymentMethod", "status", "operation", "action"];

  return {
    bodyList,
    header,
    sortData,
    totalExportTransferDebt: summaryDebt?.data?.all,
    checkedList,
    setCheckedList,
    selectedRow,
    setSelectedRow,
    detailSelectedRow,
    setDetailSelectedRow,
    pagi,
    setPagi,
    total: data?.count ?? 0,
  };
}
